// src/components/admin/CreateGalleryModal.jsx
import React, { useState, useRef, useEffect } from "react";
import api from "../../api/api";

export default function CreateGalleryModal({
  onClose,
  onSuccess,
  setNotification,
  editData // Recebe os dados para edição
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState(""); // Novo campo para o design moderno
  const [status, setStatus] = useState("publicado");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // 🔍 ZOOM
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  const inputRef = useRef(null);

  /* ================= PREENCHIMENTO PARA EDIÇÃO ================= */
  useEffect(() => {
    if (editData) {
      setTitle(editData.title || "");
      setDescription(editData.description || "");
      // Normaliza o status para bater com as opções do select
      const currentStatus = editData.status === "active" ? "publicado" : 
                            editData.status === "draft" ? "rascunho" : "publicado";
      setStatus(currentStatus);
      setPreview(editData.image_url || null);
    }
  }, [editData]);

  /* ================= DRAG & DROP ================= */
  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFile(droppedFile);
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile.type.startsWith("image/")) {
      return setNotification({
        show: true,
        type: "warning",
        message: "Apenas imagens são permitidas"
      });
    }
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  /* ================= SUBMIT (POST ou PUT) ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Se não for edição, a imagem é obrigatória
    if (!editData && !file) {
      return setNotification({
        show: true,
        type: "warning",
        message: "Selecione uma imagem"
      });
    }

    const formData = new FormData();
    if (file) formData.append("image", file);
    formData.append("title", title);
    formData.append("description", description);
    // Mapeia de volta para os termos do banco se necessário
    const finalStatus = status === "publicado" ? "active" : "draft";
    formData.append("status", finalStatus);

    try {
      setLoading(true);

      if (editData) {
        // MODO EDIÇÃO
        await api.put(`/galleries/admin/${editData.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        setNotification({ show: true, type: "success", message: "Foto atualizada com sucesso!" });
      } else {
        // MODO CRIAÇÃO
        await api.post("/galleries/admin", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        setNotification({ show: true, type: "success", message: "Foto adicionada à galeria!" });
      }

      onSuccess?.();
    } catch (error) {
      console.error(error);
      setNotification({
        show: true,
        type: "error",
        message: "Erro ao salvar imagem"
      });
    } finally {
      setLoading(false);
    }
  };

  /* ================= BLOQUEIA SCROLL NO ZOOM ================= */
  useEffect(() => {
    if (zoomOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
    return () => (document.body.style.overflow = "auto");
  }, [zoomOpen]);

  return (
    <>
      {/* ================= MODAL ================= */}
      <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm">
        <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 animate-scaleUp overflow-y-auto max-h-[95vh]">

          {/* HEADER */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-black text-gray-800">
              {editData ? "Editar imagem da galeria" : "Nova imagem da galeria"}
            </h2>
            <button onClick={onClose} className="text-gray-500 text-xl hover:text-gray-800 transition-colors">✕</button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* DRAG & DROP / PREVIEW */}
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => inputRef.current.click()}
              className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition relative group
                ${preview ? "border-indigo-400 bg-gray-50" : "border-gray-300 hover:border-indigo-400"}`}
            >
              {preview ? (
                <div className="relative">
                  <img
                    src={preview}
                    alt="Preview"
                    className="mx-auto max-h-48 rounded-lg object-contain"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-lg">
                     <span className="text-white text-xs font-bold bg-black/50 px-2 py-1 rounded">Trocar Imagem</span>
                  </div>
                  <button 
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setZoomLevel(1);
                        setZoomOpen(true);
                    }}
                    className="absolute top-2 right-2 bg-white/80 p-1.5 rounded-full shadow hover:bg-white"
                  >
                    🔍
                  </button>
                </div>
              ) : (
                <div className="py-8">
                  <p className="text-sm text-gray-500">
                    Arraste a imagem aqui ou{" "}
                    <span className="text-indigo-600 font-bold">clique para selecionar</span>
                  </p>
                </div>
              )}

              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleFile(e.target.files[0])}
              />
            </div>

            {/* TÍTULO */}
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Título</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none mt-1"
                placeholder="Ex: Cirurgia de Implante"
              />
            </div>

            {/* DESCRIÇÃO (Para o Design Moderno) */}
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Descrição (Subtítulo)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none mt-1 resize-none"
                placeholder="Texto que aparece abaixo do título..."
                rows="2"
              />
            </div>

            {/* STATUS */}
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full border rounded-xl px-3 py-2 text-sm bg-white mt-1 outline-none"
              >
                <option value="publicado">✅ Publicar Agora</option>
                <option value="rascunho">📝 Rascunho (Oculto)</option>
              </select>
            </div>

            {/* ACTIONS */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded-xl text-sm font-bold hover:bg-gray-200 transition"
              >
                Cancelar
              </button>

              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 disabled:opacity-50 shadow-lg shadow-indigo-100 transition"
              >
                {loading ? "Processando..." : editData ? "Salvar Alterações" : "Salvar Foto"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ================= ZOOM OVERLAY ================= */}
      {zoomOpen && preview && (
        <div
          className="fixed inset-0 z-[10050] bg-black/90 flex items-center justify-center"
          onClick={() => setZoomOpen(false)}
        >
          <div
            className="relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setZoomOpen(false)}
              className="absolute -top-12 right-0 bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center shadow-lg hover:bg-gray-200"
            >
              ✕
            </button>

            <img
              src={preview}
              alt="Zoom"
              style={{ transform: `scale(${zoomLevel})` }}
              className="max-w-[90vw] max-h-[85vh] transition-transform duration-200 rounded-xl shadow-2xl"
            />

            <div className="absolute -bottom-14 left-1/2 -translate-x-1/2 flex gap-3">
              <button onClick={() => setZoomLevel(z => Math.max(0.5, z - 0.2))} className="bg-white px-4 py-1 rounded-full font-bold shadow">−</button>
              <button onClick={() => setZoomLevel(1)} className="bg-white px-4 py-1 rounded-full text-sm shadow">Reset</button>
              <button onClick={() => setZoomLevel(z => Math.min(3, z + 0.2))} className="bg-white px-4 py-1 rounded-full font-bold shadow">+</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}